import { zonedDate } from '../../../../utils/date-time';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useParamsContext } from '../../../../contexts/ParamsContext';
import { CardAgendamento } from './Card';
import { useAgendaClienteContext, WaitlistEntry } from '../Context';
import { FilaEsperaService } from '../../../fila-espera/Service';
import { WaitlistStatus, WaitlistStatusLabels } from '../../../fila-espera/Enum';
import { useAppContext } from '../../../../contexts/AppContext';
import { Modal } from '../../../../components/Modal';
import { groupByMonth, isUpcomingSchedule } from './helpers';
import './MeusAgendamentos.css';
import { Calendar, Clock, History, Plus } from 'lucide-react';

const filaService = new FilaEsperaService();

type Tab = 'proximos' | 'historico';

const OPEN_WAITLIST = [WaitlistStatus.WAITING, WaitlistStatus.NOTIFIED];

const waitlistTone = (status: WaitlistStatus) => {
    if (status === WaitlistStatus.NOTIFIED) return 'blue';
    if (status === WaitlistStatus.ACCEPTED) return 'green';
    if (status === WaitlistStatus.WAITING) return 'orange';
    return 'gray';
};

export const AgendaList: React.FC = () => {
    const { setParams, action } = useParamsContext();
    const { loading, agendamentos, waitlist, loadWaitlist, loadAgendamentos } = useAgendaClienteContext();
    const { successSnackbar, errorSnackbar } = useAppContext();
    const [tab, setTab] = useState<Tab>('proximos');
    const [waitlistToCancel, setWaitlistToCancel] = useState<WaitlistEntry | null>(null);
    const [waitlistLoadingId, setWaitlistLoadingId] = useState<string | null>(null);

    // Agendamento recem-criado sempre aparece na aba de proximos.
    useEffect(() => {
        if (action === 'new') setTab('proximos');
    }, [action]);

    const { upcoming, history } = useMemo(() => {
        const now = moment();
        const sorted = [...agendamentos].sort((a, b) => moment(a.startAt).valueOf() - moment(b.startAt).valueOf());
        return {
            upcoming: sorted.filter((item) => isUpcomingSchedule(item, now)),
            history: sorted.filter((item) => !isUpcomingSchedule(item, now)).reverse(),
        };
    }, [agendamentos]);

    const openWaitlist = waitlist.filter((entry) => OPEN_WAITLIST.includes(entry.status as WaitlistStatus));
    const closedWaitlist = waitlist.filter((entry) => !OPEN_WAITLIST.includes(entry.status as WaitlistStatus));
    const offers = openWaitlist.filter((entry) => entry.status === WaitlistStatus.NOTIFIED);
    const waiting = openWaitlist.filter((entry) => entry.status !== WaitlistStatus.NOTIFIED);
    const [nextSchedule, ...laterSchedules] = upcoming;

    const handleCancelWaitlist = useCallback(async () => {
        if (!waitlistToCancel) return;
        try {
            setWaitlistLoadingId(waitlistToCancel._id);
            await filaService.cancelCliente(waitlistToCancel._id);
            successSnackbar('Você saiu da fila de espera.');
            loadWaitlist();
        } catch (error) {
            console.error('Erro ao cancelar fila:', error);
            errorSnackbar('Não foi possível sair da fila.');
        } finally {
            setWaitlistLoadingId(null);
            setWaitlistToCancel(null);
        }
    }, [waitlistToCancel, loadWaitlist, successSnackbar, errorSnackbar]);

    const handleAcceptWaitlist = useCallback(
        async (entryId: string) => {
            try {
                setWaitlistLoadingId(entryId);
                await filaService.accept(entryId);
                successSnackbar('Horário reservado a partir da fila.');
                loadWaitlist();
                loadAgendamentos();
            } catch (error) {
                console.error('Erro ao aceitar oferta:', error);
                errorSnackbar('Não foi possível aceitar a vaga.');
            } finally {
                setWaitlistLoadingId(null);
            }
        },
        [loadWaitlist, loadAgendamentos, successSnackbar, errorSnackbar],
    );

    const addSchedule = () => setParams({ action: 'add' });

    const waitlistServiceLabel = (entry: WaitlistEntry) => {
        const extras = (entry?.servicosExtras || []).map((extra) => extra?.name).filter(Boolean);
        return `${entry?.servico?.name || 'Serviço'}${extras.length ? ` + ${extras.join(', ')}` : ''}`;
    };

    const renderWaitlistRow = (entry: WaitlistEntry) => {
        const status = entry.status as WaitlistStatus;
        const start = entry?.preferredStartAt ? zonedDate(entry.preferredStartAt, entry.timezone) : null;
        const canCancel = OPEN_WAITLIST.includes(status);
        return (
            <div key={entry._id} className="sf-ma-row-wrap">
                <div className="sf-ma-row">
                    <span className="sf-ma-date" aria-hidden="true">
                        <span className="sf-ma-date-weekday">{start ? start.format('ddd').replace('.', '') : '—'}</span>
                        <span className="sf-ma-date-day">{start ? start.format('D') : '·'}</span>
                    </span>
                    <span className="sf-ma-row-body">
                        <span className="sf-ma-row-title block">{waitlistServiceLabel(entry)}</span>
                        <span className="sf-ma-row-sub block">
                            {entry?.attendant?.name || 'Qualquer profissional'}
                            {start ? ` · ${start.format('D [de] MMMM')}` : ''}
                        </span>
                        <span className="sf-ma-row-tags">
                            <span className="sf-ma-tag" data-tone={waitlistTone(status)}>
                                {WaitlistStatusLabels[status] || entry.status}
                            </span>
                        </span>
                    </span>
                    {canCancel && (
                        <button
                            type="button"
                            className="sf-ma-link is-destructive"
                            disabled={waitlistLoadingId === entry._id}
                            onClick={() => setWaitlistToCancel(entry)}
                        >
                            Sair
                        </button>
                    )}
                </div>
            </div>
        );
    };

    const renderGroups = (items: typeof agendamentos) =>
        groupByMonth(items).map((group) => (
            <section key={group.key} className="sf-ma-section">
                <div className="sf-ma-section-title">
                    <h2>{group.title}</h2>
                </div>
                <div className="sf-ma-group">
                    {group.items.map((agendamento) => (
                        <CardAgendamento key={agendamento._id} agendamento={agendamento} />
                    ))}
                </div>
            </section>
        ));

    if (loading) {
        return (
            <div className="sf-ma" aria-busy="true" aria-label="Carregando agendamentos">
                <div className="sf-ma-skeleton" style={{ height: '13rem' }} />
                <div className="sf-ma-skeleton" />
                <div className="sf-ma-skeleton" />
            </div>
        );
    }

    return (
        <div className="sf-ma">
            <div className="sf-ma-segmented" role="tablist" aria-label="Filtrar agendamentos">
                <button type="button" role="tab" aria-selected={tab === 'proximos'} onClick={() => setTab('proximos')}>
                    Próximos
                    {upcoming.length > 0 && <span className="sf-ma-segmented-count">{upcoming.length}</span>}
                </button>
                <button type="button" role="tab" aria-selected={tab === 'historico'} onClick={() => setTab('historico')}>
                    Histórico
                </button>
            </div>

            {tab === 'proximos' && (
                <div role="tabpanel" aria-label="Próximos">
                    {!nextSchedule && !offers.length && !waiting.length ? (
                        <div className="sf-ma-section sf-ma-group sf-ma-empty-wrap">
                            <div className="sf-ma-empty">
                                <span className="sf-ma-empty-icon"><Calendar aria-hidden="true" /></span>
                                <h3>Nenhum horário marcado</h3>
                                <p>Escolha o serviço, o profissional e o melhor horário para você.</p>
                                <button type="button" className="sf-ma-pill" onClick={addSchedule}>
                                    Agendar horário
                                </button>
                            </div>
                        </div>
                    ) : (
                        // Desktop: proximo horario fixo a esquerda e o restante a direita; celular: coluna unica.
                        <div className="sf-ma-layout">
                            <div className="sf-ma-aside">
                                {offers.map((entry) => (
                                    <section key={entry._id} className="sf-ma-section">
                                        <div className="sf-ma-section-title"><h2>Vaga disponível</h2></div>
                                        <div className="sf-ma-hero">
                                            <div className="sf-ma-hero-main">
                                                <div className="sf-ma-hero-service">{waitlistServiceLabel(entry)}</div>
                                                <div className="sf-ma-hero-meta">
                                                    {entry?.attendant?.name || 'Qualquer profissional'}
                                                    {entry?.preferredStartAt ? ` · ${zonedDate(entry.preferredStartAt, entry.timezone).format('dddd, D [de] MMMM')}` : ''}
                                                </div>
                                            </div>
                                            <div className="sf-ma-callout" data-tone="blue">
                                                <Clock aria-hidden="true" />
                                                <span>
                                                    {entry?.offerExpiresAt
                                                        ? `A oferta expira ${moment(entry.offerExpiresAt).fromNow()}.`
                                                        : 'Abriu um horário na fila de espera.'}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="sf-ma-pill"
                                                    disabled={waitlistLoadingId === entry._id}
                                                    onClick={() => handleAcceptWaitlist(entry._id)}
                                                >
                                                    {waitlistLoadingId === entry._id ? 'Reservando...' : 'Aceitar'}
                                                </button>
                                            </div>
                                        </div>
                                    </section>
                                ))}

                                {nextSchedule && (
                                    <section className="sf-ma-section">
                                        <div className="sf-ma-section-title"><h2>Próximo horário</h2></div>
                                        <CardAgendamento agendamento={nextSchedule} variant="hero" />
                                    </section>
                                )}
                            </div>

                            <div className="sf-ma-main">
                                {laterSchedules.length > 0 && renderGroups(laterSchedules)}

                                {waiting.length > 0 && (
                                    <section className="sf-ma-section">
                                        <div className="sf-ma-section-title"><h2>Fila de espera</h2></div>
                                        <div className="sf-ma-group">{waiting.map(renderWaitlistRow)}</div>
                                    </section>
                                )}

                                {!laterSchedules.length && !waiting.length && (
                                    <div className="sf-ma-section sf-ma-desktop-only">
                                        <div className="sf-ma-section-title"><h2>Depois disso</h2></div>
                                        <div className="sf-ma-group">
                                            <div className="sf-ma-empty is-compact">
                                                <p>Nenhum outro horário marcado.</p>
                                                <button type="button" className="sf-ma-pill" onClick={addSchedule}>
                                                    Agendar outro horário
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {tab === 'historico' && (
                <div role="tabpanel" aria-label="Histórico">
                    {history.length > 0 ? (
                        <div className="sf-ma-columns">{renderGroups(history)}</div>
                    ) : (
                        <div className="sf-ma-section sf-ma-group sf-ma-empty-wrap">
                            <div className="sf-ma-empty">
                                <span className="sf-ma-empty-icon"><History aria-hidden="true" /></span>
                                <h3>Sem histórico ainda</h3>
                                <p>Atendimentos concluídos e cancelados aparecem aqui.</p>
                            </div>
                        </div>
                    )}

                    {closedWaitlist.length > 0 && (
                        <section className="sf-ma-section sf-ma-narrow">
                            <div className="sf-ma-section-title"><h2>Fila de espera</h2></div>
                            <div className="sf-ma-group">{closedWaitlist.map(renderWaitlistRow)}</div>
                        </section>
                    )}
                </div>
            )}

            {/* Acao principal ao alcance do polegar no celular; no desktop fica no cabecalho. */}
            <div className="sf-ma-cta">
                <button type="button" onClick={addSchedule}>
                    <Plus aria-hidden="true" />
                    Agendar horário
                </button>
            </div>

            {waitlistToCancel && (
                <Modal
                    onClose={() => setWaitlistToCancel(null)}
                    title="Sair da fila de espera?"
                    subTitle={waitlistServiceLabel(waitlistToCancel)}
                >
                    <div className="flex flex-col gap-2 sm:flex-row-reverse sm:justify-start">
                        <button
                            type="button"
                            className="btn btn-error w-full sm:w-auto"
                            disabled={waitlistLoadingId === waitlistToCancel._id}
                            onClick={handleCancelWaitlist}
                        >
                            Sair da fila
                        </button>
                        <button type="button" className="btn btn-ghost w-full sm:w-auto" onClick={() => setWaitlistToCancel(null)}>
                            Continuar na fila
                        </button>
                    </div>
                </Modal>
            )}
        </div>
    );
};
